import React, { FC, forwardRef, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { commitLocalUpdate } from 'relay-runtime';
import { T, uploadFile } from 'src/shared/helpers';
import AngleUpIcon from 'src/shared/icons/angle-up-solid.svg';
import CloudUploadIcon from 'src/shared/icons/cloud-upload-alt-solid.svg';
import DiceIcon from 'src/shared/icons/dice-solid.svg';
import DownloadIcon from 'src/shared/icons/download-solid.svg';
import EllipsisVIcon from 'src/shared/icons/ellipsis-v-solid.svg';
import ExpandIcon from 'src/shared/icons/expand-solid.svg';
import InfoCircleIcon from 'src/shared/icons/info-circle-solid.svg';
import { useDropdown } from '../../../../widgets/hooks/useDropdown';
import { Icon } from '../../../../widgets/icon';

import image3dPreviewImg from 'assets/images/aistudio/image3d_preview.png';
import viewBackImg from 'assets/images/aistudio/views/view_back.png';
import viewBottomImg from 'assets/images/aistudio/views/view_bottom.png';
import viewFrontImg from 'assets/images/aistudio/views/view_front.png';
import viewLeftImg from 'assets/images/aistudio/views/view_left.png';
import viewLeft45Img from 'assets/images/aistudio/views/view_left_45.png';
import viewRightImg from 'assets/images/aistudio/views/view_right.png';
import viewRight45Img from 'assets/images/aistudio/views/view_right_45.png';
import viewTopImg from 'assets/images/aistudio/views/view_top.png';
import PaperPlaneIcon from 'src/shared/icons/paper-plane-solid.svg';
import StopIcon from 'src/shared/icons/stop-solid.svg';
import TimesIcon from 'src/shared/icons/times-solid.svg';
import TrashIcon from 'src/shared/icons/trash-alt-solid.svg';
import styled, { keyframes } from 'styled-components';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { useDialogManager } from '../../../../widgets/dialog';
import { showErrorNotification } from '../../../../widgets/notificationToaster';
import { LocalModel3DItem } from '../../assetLibrary/assetItem';
import { MAX_3D_IMAGE_DIMENSION, MAX_3D_UPLOAD_SIZE_BYTES, MAX_3D_UPLOAD_SIZE_MB } from '../../constants';
import { useGenerationPreflight } from '../../hooks/useGenerationPreflight';
import { getModel3DTaskParameterInput, getSupportedModel3DParameters, Model3DParameterValues } from '../../modelGeneration3DParameters';
import aiStudioApi from '../../tasks';
import { ModelGeneration3DModel, ToolType } from '../../types';
import { downloadFile } from '../../utils/downloadHelpers';
import { fileToBase64, getImageDimensions } from '../../utils/imageHelpers';
import { FairUseLimitDialog } from '../dialogs/fairUseLimitDialog';
import { Local3DPreviewDialog } from '../dialogs/local3DPreviewDialog';
import { PromptInput } from '../inputs/promptInput';
import { GeneratedAssetItem } from '../layout/generatedAssetsPanel';
import { AiStudioHelpLink, AiStudioHelpRow } from '../shared/aiStudioHelpLink';
import { ImageUploadZones } from '../shared/imageUploadZones';
import { ActionButton, DropdownMenuItem } from '../shared/sharedStyles';
import { getDefaultModelGeneration3DModel, isModelGeneration3DModelSupported, Model3DSettingsSidebar } from './model3DSettingsSidebar';

const GlbViewer = React.lazy(() => import('../shared/glbViewer'));

/* ─── Agent options ──────────────────────────────────────────────────────── */

interface AgentOption {
    id: string;
    label: string;
    description: string;
}

const AGENTS: AgentOption[] = [
    { id: 'text-to-3d', label: 'Text to 3D', description: 'Create 3D models from descriptions' },
    { id: 'image-to-3d', label: 'Image to 3D', description: 'Convert images to 3D models' },
    { id: 'multi-views-to-3d', label: 'Multi-views to 3D', description: 'Create 3D from multiple angles' }
];

/* ─── AgentSelector ──────────────────────────────────────────────────────── */

/** Matches the DropdownButton style used by presetDropdown */
const SelectTrigger = styled.button<{ $open: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px 8px 12px;
    border: 1px solid ${(props) => (props.$open ? '#8b5cf6' : props.theme.app.borderColor)};
    border-radius: 24px;
    background: ${(props) => (props.$open ? 'rgba(139,92,246,0.05)' : props.theme.app.backgroundColorBase)};
    color: ${(props) => props.theme.app.textColor};
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        border-color: #8b5cf6;
    }
`;

const TriggerLabel = styled.span<{ $selected: boolean }>`
    color: ${(props) => (props.$selected ? '#7c3aed' : props.theme.app.textColor)};
`;

const SelectAngle = styled.span<{ $open: boolean }>`
    display: flex;
    align-items: center;
    transform: ${(props) => (props.$open ? 'rotate(0deg)' : 'rotate(180deg)')};
    transition: transform 0.2s ease;
    color: ${(props) => (props.$open ? '#8b5cf6' : '#9ca3af')};

    ${Icon} {
        width: 10px;
        height: 10px;
        fill: currentColor;
    }
`;

const SelectWrapper = styled.div`
    position: relative;
`;

/** White dropdown — opens upward above the trigger */
const SelectDropdown = styled.div`
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    width: 290px;
    background: ${(props) => props.theme.app.backgroundColorBase};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 14px;
    box-shadow:
        0 -4px 20px rgba(0, 0, 0, 0.08),
        0 2px 8px rgba(0, 0, 0, 0.06);
    z-index: 200;
    overflow: hidden;
    padding: 6px;
`;

const SelectItem = styled.button<{ $active: boolean }>`
    width: 100%;
    text-align: left;
    padding: 10px 12px;
    border: none;
    border-radius: 10px;
    background: ${(props) => (props.$active ? 'rgba(139,92,246,0.08)' : 'transparent')};
    cursor: pointer;
    transition: background 0.14s ease;

    &:hover {
        background: ${(props) => (props.$active ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.04)')};
    }
`;

const SelectItemLabel = styled.div<{ $active: boolean }>`
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => (props.$active ? '#7c3aed' : props.theme.app.textColor)};
    margin-bottom: 2px;
`;

const SelectItemDesc = styled.div`
    font-size: 11px;
    color: #9ca3af;
`;

const AgentSelector: FC<{ value: string; onChange: (id: string) => void }> = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const current = AGENTS.find((a) => a.id === value) ?? AGENTS[2];

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <SelectWrapper ref={wrapperRef}>
            {open && (
                <SelectDropdown>
                    {AGENTS.map((agent) => (
                        <SelectItem
                            key={agent.id}
                            $active={agent.id === value}
                            onClick={() => {
                                onChange(agent.id);
                                setOpen(false);
                            }}
                        >
                            <SelectItemLabel $active={agent.id === value}>{T._(agent.label, 'Admin')}</SelectItemLabel>
                            <SelectItemDesc>{T._(agent.description, 'Admin')}</SelectItemDesc>
                        </SelectItem>
                    ))}
                </SelectDropdown>
            )}

            <SelectTrigger $open={open} onClick={() => setOpen((v) => !v)}>
                <TriggerLabel $selected={!!value}>{T._(current.label, 'Admin')}</TriggerLabel>
                <SelectAngle $open={open}>
                    <Icon>
                        <AngleUpIcon />
                    </Icon>
                </SelectAngle>
            </SelectTrigger>
        </SelectWrapper>
    );
};

/* ─── Image drop zone ────────────────────────────────────────────────────── */

const DropZoneWrapper = styled.div<{ $dragging: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: 2px dashed ${(props) => (props.$dragging ? '#3b82f6' : '#d1d5db')};
    border-radius: 16px;
    padding: 24px 16px;
    background: ${(props) => (props.$dragging ? 'rgba(59, 130, 246, 0.1)' : 'transparent')};
    cursor: pointer;
    transition:
        border-color 0.2s ease,
        background 0.2s ease;

    &:hover {
        border-color: #3b82f6;
        background: rgba(59, 130, 246, 0.08);
    }
`;

const DropZoneIconWrap = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: linear-gradient(135deg, #1d4ed8 0%, #3b82f6 50%, #60a5fa 100%);
    display: flex;
    align-items: center;
    justify-content: center;

    ${Icon} {
        width: 22px;
        height: 22px;
        fill: #ffffff;
        color: #ffffff;
    }
`;

const DropZoneTitle = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: #1d4ed8;
    margin: 0;
    text-align: center;
`;

const DropZoneSubtitle = styled.p`
    font-size: 11px;
    color: #60a5fa;
    margin: 0;
    text-align: center;
    line-height: 1.5;
`;

const HiddenInput = styled.input`
    display: none;
`;

const SuggestionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    position: relative;
    margin-top: 2px;

    ${Icon} {
        width: 12px;
        height: 12px;
        fill: #3b82f6;
        flex-shrink: 0;
    }
`;

const SuggestionsLabel = styled.span`
    font-size: 11px;
    color: #3b82f6;
    font-weight: 500;
`;

const SuggestionsTooltip = styled.div<{ $visible: boolean }>`
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translateX(-50%);
    width: 420px;
    background: #ffffff;
    border: 1px solid #dbeafe;
    border-radius: 16px;
    box-shadow:
        0 8px 32px rgba(59, 130, 246, 0.14),
        0 2px 8px rgba(0, 0, 0, 0.06);
    padding: 16px;
    z-index: 300;
    pointer-events: none;
    opacity: ${(props) => (props.$visible ? 1 : 0)};
    transition: opacity 0.16s ease;
`;

const TooltipTitle = styled.p`
    font-size: 13px;
    font-weight: 700;
    color: ${(props) => props.theme.app.textColor};
    margin: 0 0 8px 0;
`;

const TooltipDesc = styled.p`
    font-size: 12px;
    color: #6b7280;
    margin: 0 0 12px 0;
    line-height: 1.5;
`;

const TooltipBody = styled.div`
    display: flex;
    gap: 12px;
    align-items: flex-start;
`;

const TooltipImage = styled.img`
    width: 90px;
    height: 90px;
    border-radius: 10px;
    object-fit: cover;
    background: #07111d;
    flex-shrink: 0;
`;

const TooltipBullets = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
    max-height: 90px;
    overflow: hidden;
`;

const TooltipBullet = styled.li`
    font-size: 12px;
    color: #374151;
    line-height: 1.4;
    display: flex;
    align-items: flex-start;
    gap: 6px;

    &::before {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: linear-gradient(135deg, #7c3aed, #ec4899);
        flex-shrink: 0;
        margin-top: 4px;
    }
`;

const UPLOAD_BULLETS = [
    'Simple background (preferably a solid color)',
    'Does not contain text',
    'Single subject, front view, full body',
    'The main body should not be too small'
];

type MvSlotId = 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom' | 'left_front' | 'right_front';

const MV_SLOT_ORDER: { id: MvSlotId; label: string; required?: boolean }[] = [
    { id: 'front', label: 'Front', required: true },
    { id: 'back', label: 'Back' },
    { id: 'left', label: 'Left' },
    { id: 'right', label: 'Right' },
    { id: 'left_front', label: 'Left 45°' },
    { id: 'right_front', label: 'Right 45°' },
    { id: 'top', label: 'Top' },
    { id: 'bottom', label: 'Bottom' }
];

const ImageDropZone: FC<{ onFile?: (file: File) => void }> = ({ onFile }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState(false);

    const handleFile = useCallback(
        async (file: File) => {
            if (!file.type.startsWith('image/')) return;
            if (file.size > MAX_3D_UPLOAD_SIZE_BYTES) {
                showErrorNotification({ message: T._(`File size must not exceed ${MAX_3D_UPLOAD_SIZE_MB} MB.`, 'Admin') });
                return;
            }
            try {
                const { width, height } = await getImageDimensions(file);
                if (width > MAX_3D_IMAGE_DIMENSION || height > MAX_3D_IMAGE_DIMENSION) {
                    showErrorNotification({
                        message: T._(`Image resolution must not exceed ${MAX_3D_IMAGE_DIMENSION}x${MAX_3D_IMAGE_DIMENSION} pixels.`, 'Admin')
                    });
                    return;
                }
            } catch {
                showErrorNotification({ message: T._('Unable to read image. Please try a different file.', 'Admin') });
                return;
            }
            onFile?.(file);
        },
        [onFile]
    );

    const onDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragging(true);
    }, []);

    const onDragLeave = useCallback(() => setDragging(false), []);

    const onDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setDragging(false);
            const file = e.dataTransfer.files[0];
            if (file) handleFile(file);
        },
        [handleFile]
    );

    const onClick = useCallback(() => inputRef.current?.click(), []);

    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = '';
        },
        [handleFile]
    );

    return (
        <DropZoneWrapper $dragging={dragging} onClick={onClick} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
            <DropZoneIconWrap>
                <Icon>
                    <CloudUploadIcon />
                </Icon>
            </DropZoneIconWrap>
            <DropZoneTitle>{T._('Drop image or click to upload', 'Admin')}</DropZoneTitle>
            <DropZoneSubtitle>
                {T._(
                    `Supports PNG/JPG/JPEG/WebP formats, with a maximum file size of ${MAX_3D_UPLOAD_SIZE_MB}MB and a minimum resolution of 128×128, and a maximum resolution of 4096×4096.`,
                    'Admin'
                )}
            </DropZoneSubtitle>
            <HiddenInput ref={inputRef} type='file' accept='image/png,image/jpeg,image/webp' onChange={onChange} />

            <SuggestionsRow onMouseEnter={() => setTooltipVisible(true)} onMouseLeave={() => setTooltipVisible(false)} onClick={(e) => e.stopPropagation()}>
                <Icon>
                    <InfoCircleIcon />
                </Icon>
                <SuggestionsLabel>{T._('Image upload suggestions', 'Admin')}</SuggestionsLabel>

                <SuggestionsTooltip $visible={tooltipVisible}>
                    <TooltipTitle>{T._('Image upload suggestions', 'Admin')}</TooltipTitle>
                    <TooltipDesc>
                        {T._(
                            `Supports PNG/JPG/JPEG/WebP formats, with a maximum file size of ${MAX_3D_UPLOAD_SIZE_MB}MB and a minimum resolution of 128×128, and a maximum resolution of 4096×4096.`,
                            'Admin'
                        )}
                    </TooltipDesc>
                    <TooltipBody>
                        <TooltipImage src={image3dPreviewImg} alt='3D model example' />
                        <TooltipBullets>
                            {UPLOAD_BULLETS.map((b) => (
                                <TooltipBullet key={b}>{T._(b, 'Admin')}</TooltipBullet>
                            ))}
                        </TooltipBullets>
                    </TooltipBody>
                </SuggestionsTooltip>
            </SuggestionsRow>
        </DropZoneWrapper>
    );
};

/* ─── Multi-view drop zone ───────────────────────────────────────────────── */

interface ViewSlotDef {
    id: string;
    label: string;
    required?: boolean;
    thumbnail: string;
}

const VIEW_SLOTS: ViewSlotDef[] = [
    { id: 'front', label: 'Front', required: true, thumbnail: viewFrontImg },
    { id: 'back', label: 'Back', thumbnail: viewBackImg },
    { id: 'left', label: 'Left', thumbnail: viewLeftImg },
    { id: 'right', label: 'Right', thumbnail: viewRightImg },
    { id: 'left_front', label: 'Left 45°', thumbnail: viewLeft45Img },
    { id: 'right_front', label: 'Right 45°', thumbnail: viewRight45Img },
    { id: 'top', label: 'Top', thumbnail: viewTopImg },
    { id: 'bottom', label: 'Bottom', thumbnail: viewBottomImg }
];

interface MVSlotState {
    preview: string | null;
    loading: boolean;
    progress: number;
}

const EMPTY_SLOT: MVSlotState = { preview: null, loading: false, progress: 0 };

/** Wraps DropZoneWrapper with tighter spacing for the multi-view layout */
const MultiViewDropZoneWrapper = styled(DropZoneWrapper)`
    gap: 8px;
    padding: 16px;
    justify-content: flex-start;
    cursor: default;
`;

const SlotsGrid = styled.div`
    display: flex;
    flex-direction: row;
    gap: 6px;
    width: 100%;
`;

// MVSlot must be declared BEFORE MVSlotRemove / MVSlotPlus so their hover
// selectors can reference it via ${MVSlot}:hover &
const MVSlot = styled.div<{ $dragging: boolean; $filled: boolean; $required: boolean }>`
    flex: 1;
    height: 82px;
    border-radius: 14px;
    border: 1px solid ${(props) => (props.$dragging ? '#3b82f6' : props.$filled ? 'transparent' : props.$required ? '#bfdbfe' : '#e5e7eb')};
    background: ${(props) =>
        props.$filled
            ? 'transparent'
            : props.$dragging
              ? 'rgba(59, 130, 246, 0.08)'
              : props.$required
                ? 'linear-gradient(135deg, #eff6ff 0%, #f8fbff 55%, #ffffff 100%)'
                : 'linear-gradient(180deg, #ffffff 0%, #fafbfd 100%)'};
    box-shadow: ${(props) => (props.$filled ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.025)')};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    position: relative;
    overflow: hidden;
    cursor: ${(props) => (props.$filled ? 'default' : 'pointer')};
    transition:
        transform 0.18s ease,
        box-shadow 0.18s ease,
        border-color 0.15s ease,
        background 0.15s ease;

    &:hover {
        transform: ${(props) => (props.$filled ? 'none' : 'translateY(-1px)')};
        border-color: ${(props) => (props.$filled ? 'transparent' : '#3b82f6')};
        background: ${(props) => (props.$filled ? 'transparent' : 'linear-gradient(135deg, #dbeafe 0%, #e8f0ff 100%)')};
        box-shadow: ${(props) => (props.$filled ? 'none' : '0 2px 6px rgba(59, 130, 246, 0.08)')};
    }
`;

/** Small blue pill shown below the label on the FRONT (required) slot */
const MVSlotRequiredPill = styled.span`
    padding: 2px 6px;
    border-radius: 7px;
    background: #3b82f6;
    color: #ffffff;
    font-size: 8px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    pointer-events: none;
`;

/** Neutral muted pill shown below the label on non-required slots */
const MVSlotOptionalPill = styled.span`
    padding: 2px 6px;
    border-radius: 7px;
    background: #f3f4f6;
    color: #9ca3af;
    font-size: 8px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    pointer-events: none;
`;

/** Blue + that fades in on card hover (only on empty / hoverable slots) */
const MVSlotPlus = styled.span`
    position: absolute;
    top: 6px;
    right: 8px;
    color: #3b82f6;
    font-size: 14px;
    line-height: 1;
    font-weight: 600;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;

    ${MVSlot}:hover & {
        opacity: 1;
    }
`;

/** Wireframe parallelepiped / visual preview container */
const MVSlotIcon = styled.div`
    width: 42px;
    height: 34px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 100%;
        height: 100%;
        display: block;
    }

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        display: block;
    }
`;

/** Wrapper for the contained thumbnail + remove button */
const MVSlotThumbWrap = styled.div`
    position: relative;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
`;

const MVSlotThumbRound = styled.img`
    width: 44px;
    height: 44px;
    border-radius: 8px;
    object-fit: cover;
    display: block;
`;

/** Full-cover image — fills the entire slot without any layout shift */
const MVSlotCoverImg = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const MVSlotRemove = styled.button`
    position: absolute;
    top: 4px;
    right: 4px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1.5px solid #ffffff;
    background: rgba(17, 24, 39, 0.7);
    color: #ffffff;
    font-size: 10px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    padding: 0;
    transition: background 0.15s ease;

    &:hover {
        background: #111827;
    }
`;

const MVSlotLabel = styled.span<{ $emphasized?: boolean }>`
    font-size: 12px;
    font-weight: ${(props) => (props.$emphasized ? 500 : 400)};
    color: ${(props) => (props.$emphasized ? '#1e40af' : '#374151')};
    display: flex;
    align-items: center;
    gap: 2px;
    line-height: 1;
`;

const MVSlotProgress = styled.div<{ $progress: number }>`
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    width: ${(props) => props.$progress}%;
    background: linear-gradient(90deg, #1d4ed8, #60a5fa);
    transition: width 0.08s linear;
    z-index: 2;
`;

const MultiViewInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    line-height: 1.4;
    position: relative;
    cursor: pointer;

    ${Icon} {
        width: 15px;
        height: 15px;
        fill: #3b82f6;
        flex-shrink: 0;
    }
`;

export const MultiViewDropZone: FC<{
    onAnyFilled?: (filled: boolean) => void;
    onAnyLoading?: (loading: boolean) => void;
    onSlotsChange?: (slots: Record<string, { file: File; previewUrl: string; name: string } | null>) => void;
    /** Pre-seed slots on mount (e.g. when workspace opens with multi-view files already picked on the landing). */
    initialSlots?: Record<string, { file: File; previewUrl: string; name: string } | null>;
}> = ({ onAnyFilled, onAnyLoading, onSlotsChange, initialSlots }) => {
    const previewUrlsRef = useRef<Set<string>>(new Set());
    // Build seeded state up front so the dropzone owns its preview URLs. We create fresh
    // object URLs from the seeded files (rather than reusing the URL from initialSlots) so
    // the parent can revoke its own copies without leaving us with a dangling preview.
    const seededRef = useRef<{ slots: Record<string, MVSlotState>; names: Record<string, string>; files: Record<string, File> } | undefined>(undefined);
    if (seededRef.current === undefined) {
        const slotsInit = Object.fromEntries(VIEW_SLOTS.map((s) => [s.id, { ...EMPTY_SLOT }])) as Record<string, MVSlotState>;
        const namesInit: Record<string, string> = {};
        const filesInit: Record<string, File> = {};
        if (initialSlots) {
            for (const key of Object.keys(initialSlots)) {
                const seeded = initialSlots[key];
                if (seeded) {
                    const url = URL.createObjectURL(seeded.file);
                    previewUrlsRef.current.add(url);
                    slotsInit[key] = { preview: url, loading: false, progress: 100 };
                    namesInit[key] = seeded.name;
                    filesInit[key] = seeded.file;
                }
            }
        }
        seededRef.current = { slots: slotsInit, names: namesInit, files: filesInit };
    }
    const [slots, setSlots] = useState<Record<string, MVSlotState>>(seededRef.current.slots);
    const [draggingSlot, setDraggingSlot] = useState<string | null>(null);
    const [mvTooltipVisible, setMvTooltipVisible] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const targetSlotRef = useRef<string>(VIEW_SLOTS[0].id);
    const slotNamesRef = useRef<Record<string, string>>(seededRef.current.names);
    const slotFilesRef = useRef<Record<string, File>>(seededRef.current.files);

    useEffect(() => {
        const urls = previewUrlsRef.current;
        return () => urls.forEach((url) => URL.revokeObjectURL(url));
    }, []);

    useEffect(() => {
        onAnyFilled?.(Object.values(slots).some((s) => s.preview !== null));
        onAnyLoading?.(Object.values(slots).some((s) => s.loading));
        if (onSlotsChange) {
            const exposed: Record<string, { file: File; previewUrl: string; name: string } | null> = {};
            for (const key of Object.keys(slots)) {
                const s = slots[key];
                const file = slotFilesRef.current[key];
                exposed[key] = s.preview && file ? { file, previewUrl: s.preview, name: slotNamesRef.current[key] || '' } : null;
            }
            onSlotsChange(exposed);
        }
    }, [slots, onAnyFilled, onAnyLoading, onSlotsChange]);

    const loadFile = useCallback(async (slotId: string, file: File) => {
        if (!file.type.startsWith('image/')) return;
        if (file.size > MAX_3D_UPLOAD_SIZE_BYTES) {
            showErrorNotification({ message: T._(`File size must not exceed ${MAX_3D_UPLOAD_SIZE_MB} MB.`, 'Admin') });
            return;
        }
        try {
            const { width, height } = await getImageDimensions(file);
            if (width > MAX_3D_IMAGE_DIMENSION || height > MAX_3D_IMAGE_DIMENSION) {
                showErrorNotification({
                    message: T._(`Image resolution must not exceed ${MAX_3D_IMAGE_DIMENSION}x${MAX_3D_IMAGE_DIMENSION} pixels.`, 'Admin')
                });
                return;
            }
        } catch {
            showErrorNotification({ message: T._('Unable to read image. Please try a different file.', 'Admin') });
            return;
        }
        const url = URL.createObjectURL(file);
        previewUrlsRef.current.add(url);
        slotNamesRef.current[slotId] = file.name;
        slotFilesRef.current[slotId] = file;
        setSlots((prev) => {
            const old = prev[slotId].preview;
            if (old) URL.revokeObjectURL(old);
            return { ...prev, [slotId]: { preview: url, loading: true, progress: 0 } };
        });
        let prog = 0;
        const iv = setInterval(() => {
            prog = Math.min(prog + 14, 100);
            setSlots((prev) => ({ ...prev, [slotId]: { ...prev[slotId], progress: prog } }));
            if (prog >= 100) {
                clearInterval(iv);
                setSlots((prev) => ({ ...prev, [slotId]: { ...prev[slotId], loading: false } }));
            }
        }, 55);
    }, []);

    const removeSlot = useCallback((slotId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        delete slotFilesRef.current[slotId];
        setSlots((prev) => {
            const old = prev[slotId].preview;
            if (old) URL.revokeObjectURL(old);
            return { ...prev, [slotId]: { ...EMPTY_SLOT } };
        });
    }, []);

    const onSlotClick = useCallback((slotId: string) => {
        targetSlotRef.current = slotId;
        inputRef.current?.click();
    }, []);

    const onInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) loadFile(targetSlotRef.current, file);
            e.target.value = '';
        },
        [loadFile]
    );

    const onDragOver = useCallback((e: React.DragEvent, slotId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDraggingSlot(slotId);
    }, []);

    const onDragLeave = useCallback((e: React.DragEvent) => {
        e.stopPropagation();
        setDraggingSlot(null);
    }, []);

    const onDrop = useCallback(
        (e: React.DragEvent, slotId: string) => {
            e.preventDefault();
            e.stopPropagation();
            setDraggingSlot(null);
            const file = e.dataTransfer.files[0];
            if (file) loadFile(slotId, file);
        },
        [loadFile]
    );

    // Unified layout — the cards themselves flip between empty (icon + label + pill)
    // and filled (uploaded image cover) states individually, so we render the same
    // dashed drop zone regardless of whether any slot has an image yet.
    return (
        <MultiViewDropZoneWrapper $dragging={false}>
            <DropZoneIconWrap>
                <Icon>
                    <CloudUploadIcon />
                </Icon>
            </DropZoneIconWrap>
            <DropZoneTitle>{T._('Drop images or click to upload', 'Admin')}</DropZoneTitle>

            <SlotsGrid>
                {VIEW_SLOTS.map((slot) => {
                    const state = slots[slot.id];
                    const isDragging = draggingSlot === slot.id;
                    const filled = !!state.preview;
                    return (
                        <MVSlot
                            key={slot.id}
                            $dragging={isDragging}
                            $filled={filled}
                            $required={!!slot.required}
                            onClick={() => !filled && onSlotClick(slot.id)}
                            onDragOver={(e) => onDragOver(e, slot.id)}
                            onDragLeave={onDragLeave}
                            onDrop={(e) => onDrop(e, slot.id)}
                        >
                            {filled ? (
                                <>
                                    <MVSlotCoverImg src={state.preview!} alt={slot.label} />
                                    <MVSlotRemove onClick={(e) => removeSlot(slot.id, e)}>✕</MVSlotRemove>
                                </>
                            ) : (
                                <>
                                    <MVSlotPlus>+</MVSlotPlus>
                                    <MVSlotIcon>
                                        <img src={slot.thumbnail} alt={slot.label} />
                                    </MVSlotIcon>
                                    <MVSlotLabel $emphasized={slot.required}>{slot.label}</MVSlotLabel>
                                    {slot.required ? (
                                        <MVSlotRequiredPill>{T._('Required', 'Admin')}</MVSlotRequiredPill>
                                    ) : (
                                        <MVSlotOptionalPill>{T._('Optional', 'Admin')}</MVSlotOptionalPill>
                                    )}
                                </>
                            )}
                            {state.loading && <MVSlotProgress $progress={state.progress} />}
                        </MVSlot>
                    );
                })}
            </SlotsGrid>

            <MultiViewInfo onMouseEnter={() => setMvTooltipVisible(true)} onMouseLeave={() => setMvTooltipVisible(false)}>
                <Icon>
                    <InfoCircleIcon />
                </Icon>
                <SuggestionsLabel>{T._('Image upload suggestions', 'Admin')}</SuggestionsLabel>

                <SuggestionsTooltip $visible={mvTooltipVisible}>
                    <TooltipDesc>
                        {T._('Upload one image per view — ', 'Admin')}
                        <strong>{T._('Front is the only required angle.', 'Admin')}</strong>
                        {T._(' Additional views improve 3D reconstruction accuracy, but are optional.', 'Admin')}
                    </TooltipDesc>
                    <TooltipDesc style={{ marginBottom: 0 }}>
                        {T._(
                            `Maximum file size of ${MAX_3D_UPLOAD_SIZE_MB} MB and minimum resolution of 128x128, and a maximum resolution of 4096x4096.`,
                            'Admin'
                        )}
                    </TooltipDesc>
                </SuggestionsTooltip>
            </MultiViewInfo>

            <HiddenInput ref={inputRef} type='file' accept='image/png,image/jpeg,image/webp' onChange={onInputChange} />
        </MultiViewDropZoneWrapper>
    );
};

/* ─── Random prompts for Text to 3D ─────────────────────────────────────── */

const TEXT_TO_3D_RANDOM_PROMPTS = [
    'A white ceramic coffee mug with a simple handle, product shot ready',
    'A leather wallet with stitched edges and card slots, ecommerce style',
    'A glass perfume bottle with a gold cap and minimalist label',
    'A pair of white running sneakers with a rubber sole, front view',
    'A stainless steel water bottle with a screw-top lid, matte finish',
    'A wooden cutting board with carved handles, kitchen product',
    'A round scented candle in a glass jar with a cotton wick',
    'A sports watch with a silicone strap and digital display',
    'A cotton tote bag with short handles and a front pocket',
    'A hardcover notebook with elastic closure and ribbon bookmark',
    'A set of wireless earbuds in a compact charging case',
    'A olive oil bottle with a cork stopper and printed label'
];

const InputWithDiceWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const DiceBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 0;
    padding: 4px;
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 6px;
    overflow: hidden;
    transition:
        color 0.15s ease,
        background 0.15s ease,
        gap 0.15s ease,
        padding 0.15s ease;

    ${Icon} {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        fill: currentColor;
    }

    &:hover {
        color: #7c3aed;
        background: rgba(124, 58, 237, 0.07);
        gap: 5px;
        padding: 4px 8px 4px 6px;
    }
`;

const DiceBtnLabel = styled.span`
    font-size: 11px;
    max-width: 0;
    overflow: hidden;
    opacity: 0;
    white-space: nowrap;
    transition:
        max-width 0.2s ease,
        opacity 0.15s ease;

    ${DiceBtn}:hover & {
        max-width: 140px;
        opacity: 1;
    }
`;

const DiceRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

/* ─── 3-D viewport ───────────────────────────────────────────────────────── */

const VIEWPORT_BG = '#F5F6F7';

interface AxisLabel {
    ref: React.RefObject<HTMLSpanElement>;
    worldPos: THREE.Vector3;
    color: string;
}

interface Viewport3DProps {
    modelGenerated?: boolean;
    overlayContent?: React.ReactNode;
    agentType?: string;
}

export interface ViewportHandle {
    captureScreenshot: () => string | undefined;
}

export const Viewport3D = forwardRef<ViewportHandle, Viewport3DProps>(({ modelGenerated, overlayContent, agentType }, ref) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const xLabelRef = useRef<HTMLSpanElement>(null);
    const yLabelRef = useRef<HTMLSpanElement>(null);
    const zLabelRef = useRef<HTMLSpanElement>(null);
    const originLabelRef = useRef<HTMLSpanElement>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const shoeGroupRef = useRef<THREE.Group | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);

    useImperativeHandle(ref, () => ({
        captureScreenshot: () => {
            const renderer = rendererRef.current;
            const scene = sceneRef.current;
            const camera = cameraRef.current;
            if (!renderer || !scene || !camera) return undefined;
            const canvas = renderer.domElement;
            if (!canvas.width || !canvas.height) return undefined;
            try {
                // Force a fresh render so toDataURL captures the current scene state
                renderer.render(scene, camera);
                const url = canvas.toDataURL('image/jpeg', 0.85);
                // Reject obviously invalid / empty data URLs
                if (!url || url.length < 200 || url === 'data:,') return undefined;
                return url;
            } catch {
                return undefined;
            }
        }
    }));

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
        rendererRef.current = renderer;
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setClearColor(new THREE.Color(VIEWPORT_BG), 1);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        mount.appendChild(renderer.domElement);

        const scene = new THREE.Scene();
        sceneRef.current = scene;
        scene.background = new THREE.Color(VIEWPORT_BG);
        scene.fog = new THREE.Fog(VIEWPORT_BG, 18, 36);

        // Physically-based environment lighting
        const pmrem = new THREE.PMREMGenerator(renderer);
        scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
        pmrem.dispose();

        const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.01, 200);
        cameraRef.current = camera;
        camera.position.set(7, 5, 7);
        camera.lookAt(0, 0, 0);

        scene.add(new THREE.AmbientLight(0xffffff, 1.4));
        const sun = new THREE.DirectionalLight(0xffffff, 0.6);
        sun.position.set(6, 10, 6);
        sun.castShadow = true;
        scene.add(sun);

        // Sub-grid and main grid
        const subGrid = new THREE.GridHelper(24, 48, 0xd1d5db, 0xe5e7eb);
        (subGrid.material as THREE.LineBasicMaterial).transparent = true;
        (subGrid.material as THREE.LineBasicMaterial).opacity = 0.9;
        scene.add(subGrid);

        const mainGrid = new THREE.GridHelper(24, 12, 0x9ca3af, 0xd1d5db);
        (mainGrid.material as THREE.LineBasicMaterial).transparent = true;
        (mainGrid.material as THREE.LineBasicMaterial).opacity = 0.6;
        scene.add(mainGrid);

        // Axes — lines only, no arrow heads
        const AXIS_LEN = 3.5;
        const makeAxis = (dir: THREE.Vector3, color: number, opacity = 1) => {
            const mat = new THREE.LineBasicMaterial({ color, transparent: opacity < 1, opacity });
            const geo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), dir.clone().multiplyScalar(AXIS_LEN)]);
            return new THREE.Line(geo, mat);
        };
        scene.add(makeAxis(new THREE.Vector3(1, 0, 0), 0xef4444));
        scene.add(makeAxis(new THREE.Vector3(0, 1, 0), 0x22c55e));
        scene.add(makeAxis(new THREE.Vector3(0, 0, 1), 0x3b82f6));
        scene.add(makeAxis(new THREE.Vector3(-1, 0, 0), 0xef4444, 0.22));
        scene.add(makeAxis(new THREE.Vector3(0, 0, -1), 0x3b82f6, 0.22));

        // World origin
        const originMesh = new THREE.Mesh(
            new THREE.SphereGeometry(0.07, 16, 16),
            new THREE.MeshStandardMaterial({ color: 0x374151, roughness: 0.3, metalness: 0.1 })
        );
        scene.add(originMesh);

        const ring = new THREE.Mesh(
            new THREE.RingGeometry(0.12, 0.22, 32),
            new THREE.MeshBasicMaterial({ color: 0x6366f1, transparent: true, opacity: 0.35, side: THREE.DoubleSide })
        );
        ring.rotation.x = -Math.PI / 2;
        scene.add(ring);

        const controls = new OrbitControls(camera, renderer.domElement as unknown as HTMLElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.06;
        controls.minDistance = 2;
        controls.maxDistance = 60;
        controls.maxPolarAngle = Math.PI / 2 - 0.02;
        controls.update();

        const axisLabels: AxisLabel[] = [
            { ref: xLabelRef, worldPos: new THREE.Vector3(AXIS_LEN + 0.4, 0, 0), color: '#ef4444' },
            { ref: yLabelRef, worldPos: new THREE.Vector3(0, AXIS_LEN + 0.4, 0), color: '#22c55e' },
            { ref: zLabelRef, worldPos: new THREE.Vector3(0, 0, AXIS_LEN + 0.4), color: '#3b82f6' },
            { ref: originLabelRef, worldPos: new THREE.Vector3(0.18, 0.18, 0), color: '#6b7280' }
        ];

        const updateLabels = () => {
            const W = renderer.domElement.clientWidth;
            const H = renderer.domElement.clientHeight;
            axisLabels.forEach(({ ref, worldPos }) => {
                if (!ref.current) return;
                const v = worldPos.clone().project(camera);
                ref.current.style.left = `${(v.x * 0.5 + 0.5) * W}px`;
                ref.current.style.top = `${(-v.y * 0.5 + 0.5) * H}px`;
                ref.current.style.opacity = v.z < 1 ? '1' : '0';
            });
        };

        const onResize = () => {
            const W = mount.clientWidth;
            const H = mount.clientHeight;
            camera.aspect = W / H;
            camera.updateProjectionMatrix();
            renderer.setSize(W, H);
        };
        onResize();
        const ro = new ResizeObserver(onResize);
        ro.observe(mount);

        let rafId = 0;
        const animate = () => {
            rafId = requestAnimationFrame(animate);
            controls.update();
            if (shoeGroupRef.current) {
                shoeGroupRef.current.rotation.y += 0.004;
            }
            renderer.render(scene, camera);
            updateLabels();
        };
        animate();

        return () => {
            cancelAnimationFrame(rafId);
            ro.disconnect();
            controls.dispose();
            renderer.dispose();
            scene.clear();
            sceneRef.current = null;
            cameraRef.current = null;
            rendererRef.current = null;
            if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
        };
    }, []);

    useEffect(() => {
        if (!modelGenerated || !sceneRef.current) return;

        const scene = sceneRef.current;

        let geometry: THREE.BufferGeometry;
        let yOffset: number;
        if (agentType === 'image-to-3d') {
            geometry = new THREE.CylinderGeometry(0.9, 0.9, 1.8, 32);
            yOffset = 0.9;
        } else if (agentType === 'multi-views-to-3d') {
            geometry = new THREE.ConeGeometry(1.1, 1.9, 4);
            yOffset = 0.95;
        } else {
            // text-to-3d (default)
            geometry = new THREE.BoxGeometry(1.8, 1.8, 1.8);
            yOffset = 0.9;
        }

        const mesh = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color: 0x7c3aed, roughness: 0.3, metalness: 0.15 }));
        mesh.position.set(0, yOffset, 0);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        scene.add(mesh);
        shoeGroupRef.current = mesh as unknown as THREE.Group;

        // Animate in with ease-out-back
        mesh.scale.setScalar(0);
        const startTime = performance.now();
        const duration = 900;
        let animFrame = 0;
        const doScale = () => {
            const t = Math.min((performance.now() - startTime) / duration, 1);
            const c1 = 1.70158;
            const c3 = c1 + 1;
            const s = t === 1 ? 1 : 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
            mesh.scale.setScalar(Math.max(0, s));
            if (t < 1) animFrame = requestAnimationFrame(doScale);
        };
        animFrame = requestAnimationFrame(doScale);

        return () => {
            cancelAnimationFrame(animFrame);
            scene.remove(mesh);
            shoeGroupRef.current = null;
        };
    }, [modelGenerated, agentType]);

    return (
        <ViewportWrapper ref={mountRef}>
            <LabelLayer>
                <AxisLabel ref={xLabelRef} style={{ color: '#ef4444' }}>
                    X
                </AxisLabel>
                <AxisLabel ref={yLabelRef} style={{ color: '#22c55e' }}>
                    Y
                </AxisLabel>
                <AxisLabel ref={zLabelRef} style={{ color: '#3b82f6' }}>
                    Z
                </AxisLabel>
                <OriginLabel ref={originLabelRef}>Origin</OriginLabel>
            </LabelLayer>
            <HintsOverlay>
                <HintRow>
                    <HintKey>Drag</HintKey> Orbit
                </HintRow>
                <HintRow>
                    <HintKey>Scroll</HintKey> Zoom
                </HintRow>
                <HintRow>
                    <HintKey>Right-drag</HintKey> Pan
                </HintRow>
            </HintsOverlay>
            {overlayContent}
        </ViewportWrapper>
    );
});

/* ─── Styled components ──────────────────────────────────────────────────── */

const ViewportWrapper = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    background: ${VIEWPORT_BG};

    canvas {
        display: block;
        width: 100% !important;
        height: 100% !important;
    }
`;

const RealModelViewport = styled.div`
    width: 100%;
    height: 100%;
    position: relative;
    background: ${VIEWPORT_BG};
`;

const LabelLayer = styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;
`;

const AxisLabel = styled.span`
    position: absolute;
    font-size: 12px;
    font-weight: 700;
    font-family: ui-monospace, monospace;
    transform: translate(-50%, -50%);
    text-shadow:
        0 0 4px ${VIEWPORT_BG},
        0 0 8px ${VIEWPORT_BG};
`;

const OriginLabel = styled.span`
    position: absolute;
    font-size: 10px;
    font-weight: 500;
    font-family: ui-monospace, monospace;
    transform: translate(4px, -50%);
    text-shadow:
        0 0 4px ${VIEWPORT_BG},
        0 0 8px ${VIEWPORT_BG};
`;

const HintsOverlay = styled.div`
    position: absolute;
    top: 14px;
    right: 16px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    pointer-events: none;
`;

const HintRow = styled.div`
    font-size: 10px;
    color: #9ca3af;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const HintKey = styled.span`
    background: rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;
    border-radius: 3px;
    padding: 1px 5px;
    font-size: 9px;
    color: #6b7280;
`;

const ViewportMenuWrapper = styled.div`
    position: absolute;
    bottom: 8px;
    left: 8px;
    z-index: 20;
`;

const ViewportFullscreenBtn = styled.button`
    position: absolute;
    bottom: 8px;
    right: 8px;
    z-index: 20;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.9);
    color: #1e0a3c;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    transition:
        background 0.15s ease,
        transform 0.15s ease;

    ${Icon} {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }

    &:hover {
        background: #ffffff;
        transform: scale(1.06);
    }
`;

/* ─── Generation overlay ──────────────────────────────────────────────────── */

export const LOADING_MESSAGES = [
    'Analyzing your prompt…',
    'Initializing 3D generation engine…',
    'Building mesh topology…',
    'Generating surface details…',
    'Applying materials and textures…',
    'Optimizing geometry…',
    'Running final refinement pass…',
    'Almost ready…'
];

/* ─── Workspace ──────────────────────────────────────────────────────────── */

const WorkspaceShell = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
`;

const Workspace = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    background: linear-gradient(160deg, #fdfcff 0%, #faf8ff 50%, #fffafc 100%);
`;

const ViewportRow = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    min-height: 0;
    background: transparent;
    overflow: auto;
    gap: 8px;
    padding: 32px;

    @media (max-height: 720px) {
        padding: 20px;
    }

    @media (max-height: 660px) {
        padding: 14px;
    }
`;

const ModelResultsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    width: 100%;
    max-width: min(720px, calc(100vh - 340px));
    margin: 0 auto;

    @media (max-height: 720px) {
        max-width: min(560px, calc(100vh - 300px));
        gap: 12px;
    }

    @media (max-height: 660px) {
        max-width: min(460px, calc(100vh - 280px));
        gap: 10px;
    }
`;

const ModelCell = styled.div`
    aspect-ratio: 1;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    border: 1px solid rgba(147, 51, 234, 0.2);
    background: white;
`;

const EmptyModelCard = styled.div`
    aspect-ratio: 1;
    border-radius: 12px;
    background: white;
    border: 2px dashed #e5e7eb;
`;

const aiGradient = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

const aiShine = keyframes`
    0% { left: -100%; }
    50%, 100% { left: 100%; }
`;

const textFadeRotate = keyframes`
    0%, 100% { opacity: 0; }
    20%, 80% { opacity: 1; }
`;

const GeneratingModelCard = styled.div`
    aspect-ratio: 1;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border: 1px solid rgba(147, 51, 234, 0.2);
    background-color: #ffffff;
    background-image: linear-gradient(
        45deg,
        rgba(147, 51, 234, 0.1) 0%,
        rgba(79, 70, 229, 0.1) 25%,
        rgba(59, 130, 246, 0.1) 50%,
        rgba(16, 185, 129, 0.1) 75%,
        rgba(147, 51, 234, 0.1) 100%
    );
    background-size: 400% 400%;
    animation: ${aiGradient} 3s ease infinite;
    position: relative;
    overflow: clip;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
        animation: ${aiShine} 2s ease-in-out infinite;
    }
`;

const GeneratingCardText = styled.div`
    font-size: 12px;
    color: #78909c;
    animation: ${textFadeRotate} 3s ease-in-out infinite;
    position: relative;
    z-index: 1;
`;

const GeneratingCardProgressContainer = styled.div`
    width: 80%;
    height: 4px;
    background: rgba(147, 51, 234, 0.15);
    border-radius: 2px;
    overflow: hidden;
    position: relative;
    z-index: 1;
`;

const GeneratingCardProgressBar = styled.div<{ $width: number }>`
    height: 100%;
    width: ${(props) => props.$width}%;
    background: linear-gradient(90deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    border-radius: 2px;
    transition: width 1s ease-out;
`;

const PromptPanel = styled.div`
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 32px 32px;
    background: transparent;

    @media (max-height: 720px) {
        padding: 12px 24px 20px;
    }

    @media (max-height: 660px) {
        padding: 8px 20px 14px;
    }
`;

/** Outer rounded card — matches ControlsCard from the empty state landing */
const PromptCard = styled.div`
    width: 100%;
    max-width: 860px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid rgb(255, 255, 255);
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.45);
    box-sizing: border-box;
`;

/** Top row: prompt input + send button */
const InputRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

/** Bottom row: agent selector (left-aligned, like preset option buttons) */
const OptionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const TokenBadge = styled.div`
    margin-left: auto;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px 5px 7px;
    border-radius: 20px;
    background: linear-gradient(135deg, rgba(124, 58, 237, 0.08) 0%, rgba(168, 85, 247, 0.12) 100%);
    border: 1px solid rgba(124, 58, 237, 0.22);

    ${Icon} {
        width: 14px;
        height: 14px;
        fill: #7c3aed;
    }
`;

const TokenCount = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: #7c3aed;
    font-variant-numeric: tabular-nums;
    line-height: 1;
`;

const GenerateBtn = styled.button<{ $isGenerating?: boolean }>`
    width: 50px;
    height: 50px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: ${(p) => (p.$isGenerating ? '#ef4444' : 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%)')};
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        transform 0.2s ease,
        background 0.2s ease,
        opacity 0.18s ease;

    ${Icon} {
        width: 24px;
        height: 24px;
        fill: currentColor;
    }

    &:hover:not(:disabled) {
        transform: scale(1.05);
    }

    &:disabled {
        opacity: 0.42;
        cursor: not-allowed;
    }
`;

const PromptRowHoriz = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
`;

const InputPreviews = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
    align-items: center;
`;

const InputPreviewItem = styled.div`
    position: relative;
    height: 58px;
    min-width: 58px;
    max-width: 150px;
    flex-shrink: 0;
`;

const InputPreviewImage = styled.div<{ $clickable?: boolean }>`
    height: 100%;
    width: auto;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.backgroundColor};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
    transition: transform 0.15s ease;

    ${(props) =>
        props.$clickable &&
        `
        &:hover {
            transform: scale(1.04);
        }
    `}

    img {
        height: 100%;
        width: auto;
        max-width: 150px;
        object-fit: contain;
        display: block;
    }
`;

const ImagePreviewBackdrop = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.78);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: zoom-out;

    img {
        max-width: calc(100vw - 80px);
        max-height: calc(100vh - 80px);
        object-fit: contain;
        border-radius: 8px;
        box-shadow: 0 24px 64px rgba(0, 0, 0, 0.4);
    }
`;

const ImagePreviewClose = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: rgba(0, 0, 0, 0.75);
    }

    ${Icon} {
        width: 14px;
        height: 14px;
        fill: currentColor;
    }
`;

const InputPreviewRemove = styled.button`
    position: absolute;
    top: 0px;
    right: 0px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
        background: #dc2626;
    }

    ${Icon} {
        width: 8px;
        height: 8px;
        fill: currentColor;
    }
`;

const AddMoreInputButton = styled.button`
    min-height: 58px;
    min-width: 58px;
    max-width: 58px;
    max-height: 58px;
    border-radius: 50%;
    border: 2px dashed rgba(124, 58, 237, 0.35);
    background: rgba(124, 58, 237, 0.05);
    color: #7c3aed;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    font-weight: 300;
    line-height: 1;
    transition:
        background 0.15s ease,
        border-color 0.15s ease;

    &:hover {
        background: rgba(124, 58, 237, 0.12);
        border-color: rgba(124, 58, 237, 0.6);
    }
`;

/* ─── Texture Drawing panel ──────────────────────────────────────────────── */

const TextureTabRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 0;
`;

const TextureTab = styled.button<{ $active: boolean }>`
    flex: 1;
    padding: 8px 12px;
    border: 1px solid ${(props) => (props.$active ? '#8b5cf6' : props.theme.app.borderColor)};
    background: ${(props) => (props.$active ? 'rgba(139,92,246,0.08)' : 'transparent')};
    color: ${(props) => (props.$active ? '#7c3aed' : props.theme.app.textColor)};
    font-size: 12px;
    font-weight: ${(props) => (props.$active ? '600' : '500')};
    cursor: pointer;
    transition: all 0.16s ease;

    &:first-child {
        border-radius: 10px 0 0 10px;
    }

    &:last-child {
        border-radius: 0 10px 10px 0;
        border-left: none;
    }

    &:hover {
        background: ${(props) => (props.$active ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.04)')};
    }
`;

/** Wraps textarea + char count in a flex column that fills the InputRow */
const TextureDescribeArea = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const TextureTextArea = styled.textarea`
    width: 100%;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    padding: 12px;
    font-size: 13px;
    color: ${(props) => props.theme.app.textColor};
    background: ${(props) => props.theme.app.backgroundColor};
    resize: none;
    outline: none;
    line-height: 1.5;
    box-sizing: border-box;
    font-family: inherit;

    &:focus {
        border-color: #8b5cf6;
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const CharCount = styled.div`
    font-size: 11px;
    color: #9ca3af;
    text-align: right;
    margin-top: -4px;
`;

const RadioRow = styled.div`
    display: flex;
    gap: 8px;
`;

const RadioOption = styled.button<{ $active: boolean }>`
    flex: 1;
    padding: 6px 8px;
    border: 1px solid ${(props) => (props.$active ? '#8b5cf6' : props.theme.app.borderColor)};
    border-radius: 8px;
    background: ${(props) => (props.$active ? 'rgba(139,92,246,0.08)' : 'transparent')};
    color: ${(props) => (props.$active ? '#7c3aed' : props.theme.app.textColor)};
    font-size: 12px;
    font-weight: ${(props) => (props.$active ? '600' : '400')};
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.16s ease;

    &:hover {
        background: ${(props) => (props.$active ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.04)')};
        border-color: #8b5cf6;
    }
`;

const RadioDot = styled.div<{ $active: boolean }>`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1.5px solid ${(props) => (props.$active ? '#7c3aed' : '#d1d5db')};
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.16s ease;

    &::after {
        content: '';
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${(props) => (props.$active ? '#7c3aed' : 'transparent')};
        transition: background 0.16s ease;
    }
`;

const CompactDropWrapper = styled(DropZoneWrapper)`
    padding: 16px 12px;
    gap: 8px;
    flex: none;
`;

const CompactDropIconWrap = styled(DropZoneIconWrap)`
    width: 36px;
    height: 36px;
    border-radius: 10px;

    ${Icon} {
        width: 16px;
        height: 16px;
    }
`;

const TEXTURE_MAX_CHARS = 500;

// Multi-view upload order: index 0 is the main/front view (mapped to imageUrl); the rest
// are passed as multiViewImages with the view type at the matching index.
const MV_VIEW_TYPES = ['front', 'back', 'left', 'right', 'left_front', 'right_front', 'top', 'bottom'];

/* ─── Main component ─────────────────────────────────────────────────────── */

type ImageAgentInput = { kind: 'file'; file: File; previewUrl: string; name: string } | { kind: 'url'; url: string; previewUrl: string; name: string };

// The results grid renders a fixed 2×2 layout. New generations are prepended into the next
// free slot (newest first) instead of overwriting the previous model, mirroring the 2D tools.
const MAX_3D_SLOTS = 4;

interface Text3DWorkspaceViewProps {
    onBack: () => void;
    initialAgent?: string;
    initialPrompt?: string;
    initialImageFile?: File;
    initialImageUrl?: string;
    initialMultiViewReady?: boolean;
    openedAsset?: GeneratedAssetItem;
    onModelGenerated?: (id: string, agentId: string, label: string, thumbnailUrl?: string, prompt?: string, modelUrl?: string) => void;
    onOpenCatalog?: () => void;
    /** Multi-view File-based library pick (each slot is a File). */
    onOpenLibrary?: (onSelect: (file: File) => void) => void;
    /** Pre-seeded multi-view files from the landing view. Order must align with MV_VIEW_TYPES; the first entry is the front view. */
    initialMvFiles?: Array<{ file: File; viewType: string }>;
    /** Image-agent URL-based library pick (URL is passed straight to the 3D API). */
    onOpenImageLibrary?: (onSelect: (url: string, name?: string) => void) => void;
    /** Product image URL coming from the catalog/iframe flow. When it appears or changes,
     *  it is pushed into the image-agent and generation auto-starts (image-to-3d only). */
    productImageUrl?: string;
    modelGeneration3DModels?: readonly ModelGeneration3DModel[];
    selectedModel?: string;
    onModelChange?: (agentId: string, model: string) => void;
    parameterValues?: Model3DParameterValues;
    onParameterValuesChange?: (agentId: string, model: string, values: Model3DParameterValues) => void;
}

export const Text3DWorkspaceView: FC<Text3DWorkspaceViewProps> = ({
    onBack: _onBack,
    initialAgent,
    initialPrompt,
    initialImageFile,
    initialImageUrl,
    initialMultiViewReady,
    initialMvFiles,
    openedAsset,
    onModelGenerated,
    onOpenCatalog,
    onOpenLibrary,
    onOpenImageLibrary,
    productImageUrl,
    modelGeneration3DModels = [],
    selectedModel,
    onModelChange,
    parameterValues,
    onParameterValuesChange
}) => {
    const { runWithPreflight } = useGenerationPreflight();
    const { showDialog, closeDialog } = useDialogManager();
    const relayEnvironment = useRelayEnvironment();

    const [prompt, setPrompt] = useState(initialPrompt ?? '');
    const [selectedAgent, setSelectedAgent] = useState(openedAsset?.toolId ?? initialAgent ?? 'text-to-3d');
    const [multiViewHasImage, setMultiViewHasImage] = useState(false);
    const [multiViewLoading, setMultiViewLoading] = useState(false);
    const [imageAgentFile, setImageAgentFile] = useState<ImageAgentInput | null>(() => {
        if (initialImageFile) {
            return { kind: 'file', file: initialImageFile, previewUrl: URL.createObjectURL(initialImageFile), name: initialImageFile.name };
        }
        if (initialImageUrl) {
            return { kind: 'url', url: initialImageUrl, previewUrl: initialImageUrl, name: 'Library image' };
        }
        return null;
    });
    const imageAgentHasFile = imageAgentFile !== null;
    const [mvFiles, setMvFiles] = useState<Array<{ id: string; file: File; previewUrl: string; name: string; viewType: string }>>(() =>
        initialMvFiles && initialMvFiles.length > 0
            ? initialMvFiles.map(({ file, viewType }, i) => ({
                  id: `mv-init-${i}-${file.name}`,
                  file,
                  previewUrl: URL.createObjectURL(file),
                  name: file.name,
                  viewType
              }))
            : []
    );

    // One-shot seed for MultiViewDropZone's internal slot state. Built on first render from
    // any pre-seeded mvFiles (e.g. when the workspace mounts after clicking Generate on the landing),
    // so the dropzone renders with those views filled instead of starting empty.
    const mvInitialSlotsRef = useRef<Record<string, { file: File; previewUrl: string; name: string } | null> | undefined>(undefined);
    if (mvInitialSlotsRef.current === undefined) {
        const seed: Record<string, { file: File; previewUrl: string; name: string } | null> = {};
        for (const f of mvFiles) seed[f.viewType] = { file: f.file, previewUrl: f.previewUrl, name: f.name };
        mvInitialSlotsRef.current = seed;
    }

    const [isGenerating, setIsGenerating] = useState(false);
    const [tokens, setTokens] = useState(35);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [generationMsgIdx, setGenerationMsgIdx] = useState(0);
    const [generatedModels, setGeneratedModels] = useState<LocalModel3DItem[]>(() =>
        openedAsset
            ? [
                  {
                      id: openedAsset.id || `opened-${Date.now()}`,
                      label: openedAsset.label || 'Model 1',
                      toolId: openedAsset.toolId || initialAgent || 'text-to-3d',
                      modelUrl: openedAsset.modelUrl,
                      thumbnailUrl: openedAsset.thumbnailUrl,
                      prompt: openedAsset.prompt,
                      createdAt: openedAsset.createdAt ?? Date.now()
                  }
              ]
            : []
    );
    const [pendingAutoStart, setPendingAutoStart] = useState(() => {
        if (openedAsset) return false;
        if (initialPrompt && initialPrompt.trim().length > 0) return true;
        if (initialImageFile) return true;
        if (initialImageUrl) return true;
        if (initialMultiViewReady) return true;
        return false;
    });

    const isImageAgent = selectedAgent === 'image-to-3d';
    const isMultiViewAgent = selectedAgent === 'multi-views-to-3d';
    const selectedGenerationModel = useMemo(() => {
        if (isModelGeneration3DModelSupported(modelGeneration3DModels, selectedAgent, selectedModel)) {
            return selectedModel;
        }
        return getDefaultModelGeneration3DModel(modelGeneration3DModels, selectedAgent);
    }, [modelGeneration3DModels, selectedAgent, selectedModel]);
    const selectedGenerationModelInfo = useMemo(
        () => modelGeneration3DModels.find((model) => model.model === selectedGenerationModel && model.supportedModes.includes(selectedAgent)),
        [modelGeneration3DModels, selectedAgent, selectedGenerationModel]
    );
    const selectedTaskParameters = useMemo(
        () => getModel3DTaskParameterInput(getSupportedModel3DParameters(selectedGenerationModelInfo, selectedAgent), parameterValues),
        [selectedGenerationModelInfo, selectedAgent, parameterValues]
    );

    // Reset the uploaded image whenever the user switches to a different agent.
    // Skip on the initial mount so a pre-seeded initialImageFile isn't wiped before auto-generation runs.
    const agentResetMountRef = useRef(true);
    useEffect(() => {
        if (agentResetMountRef.current) {
            agentResetMountRef.current = false;
            return;
        }
        setImageAgentFile((prev) => {
            if (prev?.kind === 'file') URL.revokeObjectURL(prev.previewUrl);
            return null;
        });
    }, [selectedAgent]);

    useEffect(() => {
        // Revoke on unmount
        return () => {
            if (imageAgentFile?.kind === 'file') URL.revokeObjectURL(imageAgentFile.previewUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleImageAgentUpload = useCallback((file: File) => {
        setImageAgentFile((prev) => {
            if (prev?.kind === 'file') URL.revokeObjectURL(prev.previewUrl);
            return { kind: 'file', file, previewUrl: URL.createObjectURL(file), name: file.name };
        });
    }, []);

    // Picking an image from the library is a commitment gesture — auto-start generation
    // as soon as the input is set (mirrors the landing-view auto-start behavior).
    // URL-mode bypasses the client-side fetch so CORS on the library CDN can't block generation.
    const autoStartOnFileRef = useRef(false);
    const handleGenerateRef = useRef<() => void>(() => {});
    const handleImageAgentLibrarySelect = useCallback(() => {
        onOpenImageLibrary?.((url, name) => {
            autoStartOnFileRef.current = true;
            setImageAgentFile((prev) => {
                if (prev?.kind === 'file') URL.revokeObjectURL(prev.previewUrl);
                return { kind: 'url', url, previewUrl: url, name: name ?? 'Library image' };
            });
        });
    }, [onOpenImageLibrary]);

    useEffect(() => {
        if (autoStartOnFileRef.current && imageAgentFile && !isGenerating) {
            autoStartOnFileRef.current = false;
            handleGenerateRef.current();
        }
    }, [imageAgentFile, isGenerating]);

    // Sync a product image URL (from the catalog/iframe flow) into the image agent.
    // Runs only on transitions of productImageUrl — after a user-initiated remove, it won't
    // restore the image unless a new product URL arrives.
    // Note: we deliberately do NOT auto-start generation here. The user opened AI Studio with
    // a product loaded, but generation should only start when they explicitly click Generate.
    const lastSyncedProductUrlRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!productImageUrl || selectedAgent !== 'image-to-3d') return;
        if (productImageUrl === lastSyncedProductUrlRef.current) return;
        lastSyncedProductUrlRef.current = productImageUrl;
        setImageAgentFile((prev) => {
            if (prev?.kind === 'file') URL.revokeObjectURL(prev.previewUrl);
            return { kind: 'url', url: productImageUrl, previewUrl: productImageUrl, name: 'Product image' };
        });
    }, [productImageUrl, selectedAgent]);

    const handleImageAgentRemove = useCallback(() => {
        setImageAgentFile((prev) => {
            if (prev?.kind === 'file') URL.revokeObjectURL(prev.previewUrl);
            return null;
        });
    }, []);

    // Reset mvFiles when the user switches to a different agent.
    // Skip on the initial mount so pre-seeded initialMvFiles aren't wiped before auto-generation runs.
    const mvResetMountRef = useRef(true);
    useEffect(() => {
        if (mvResetMountRef.current) {
            mvResetMountRef.current = false;
            return;
        }
        setMvFiles((prev) => {
            prev.forEach((f) => URL.revokeObjectURL(f.previewUrl));
            return [];
        });
    }, [selectedAgent]);

    // Keep multiViewHasImage in sync with mvFiles for downstream logic
    useEffect(() => {
        setMultiViewHasImage(mvFiles.length > 0);
    }, [mvFiles]);

    const generatedCountRef = useRef(0);
    const generationTimersRef = useRef<{
        msg: ReturnType<typeof setInterval> | null;
        progress: ReturnType<typeof setInterval> | null;
        finalize: ReturnType<typeof setTimeout> | null;
    }>({ msg: null, progress: null, finalize: null });
    // Tracks whether the user stopped the in-flight generation. The API call cannot be
    // aborted, so we check this flag once it resolves and discard the result if true.
    const stoppedRef = useRef(false);

    const handleStopGenerate = useCallback(() => {
        const t = generationTimersRef.current;
        if (t.msg) clearInterval(t.msg);
        if (t.progress) clearInterval(t.progress);
        if (t.finalize) clearTimeout(t.finalize);
        generationTimersRef.current = { msg: null, progress: null, finalize: null };
        stoppedRef.current = true;
        setIsGenerating(false);
        setPendingAutoStart(false);
        setGenerationProgress(0);
        setGenerationMsgIdx(0);
    }, []);

    const executeGeneration = useCallback(async () => {
        if (selectedAgent === 'text-to-3d' && !prompt.trim()) return;
        if (selectedAgent === 'image-to-3d' && !imageAgentFile) return;
        if (selectedAgent === 'multi-views-to-3d' && mvFiles.length === 0) return;
        if (!selectedGenerationModel) {
            showErrorNotification({ message: T._('Select a 3D generation model before generating.', 'Admin') });
            return;
        }
        stoppedRef.current = false;
        setIsGenerating(true);
        setGenerationProgress(0);
        setGenerationMsgIdx(0);

        let progress = 0;

        const msgTimer = setInterval(() => {
            setGenerationMsgIdx((prev) => (prev + 1) % LOADING_MESSAGES.length);
        }, 2100);
        generationTimersRef.current.msg = msgTimer;

        // Simulated progress bar — capped at 95% while the real API call is in flight.
        // Paced to reach ~95% over ~2 minutes (avg ~0.8%/s at 1s ticks).
        const progressTimer = setInterval(() => {
            progress += Math.random() * 0.8 + 0.4;
            if (progress >= 95) progress = 95;
            setGenerationProgress(Math.floor(progress));
        }, 1000);
        generationTimersRef.current.progress = progressTimer;

        const stopTimers = () => {
            if (generationTimersRef.current.msg) {
                clearInterval(generationTimersRef.current.msg);
                generationTimersRef.current.msg = null;
            }
            if (generationTimersRef.current.progress) {
                clearInterval(generationTimersRef.current.progress);
                generationTimersRef.current.progress = null;
            }
        };

        try {
            let result: Awaited<ReturnType<typeof aiStudioApi.modelGeneration3D>> | null = null;
            if (selectedAgent === 'text-to-3d') {
                result = await aiStudioApi.modelGeneration3D(
                    {
                        model: selectedGenerationModel,
                        mode: 'text-to-3d',
                        prompt: prompt.trim(),
                        ...selectedTaskParameters,
                        saveToLibrary: true
                    },
                    'text-to-3d'
                );
            } else if (selectedAgent === 'image-to-3d' && imageAgentFile) {
                const imageUrl = imageAgentFile.kind === 'file' ? await fileToBase64(imageAgentFile.file) : imageAgentFile.url;
                result = await aiStudioApi.modelGeneration3D(
                    {
                        model: selectedGenerationModel,
                        mode: 'image-to-3d',
                        imageUrl,
                        ...selectedTaskParameters,
                        saveToLibrary: true
                    },
                    'image-to-3d'
                );
            } else if (selectedAgent === 'multi-views-to-3d' && mvFiles.length > 0) {
                // The 'front' entry (or the first file if none is tagged) is the main view → imageUrl.
                const frontIdx = Math.max(
                    0,
                    mvFiles.findIndex((f) => f.viewType === 'front')
                );
                const front = mvFiles[frontIdx];
                const others = mvFiles.filter((_, i) => i !== frontIdx);
                const frontUrl = await uploadFile(front.file);
                const multiViewImages = await Promise.all(
                    others.map(async (f) => ({
                        viewType: f.viewType,
                        viewImageUrl: await uploadFile(f.file)
                    }))
                );
                result = await aiStudioApi.modelGeneration3D(
                    {
                        model: selectedGenerationModel,
                        mode: 'multi-views-to-3d',
                        imageUrl: frontUrl,
                        multiViewImages,
                        ...selectedTaskParameters,
                        saveToLibrary: true
                    },
                    'multi-views-to-3d'
                );
            }

            stopTimers();

            // The user stopped the task while the API call was in flight — discard the result.
            if (stoppedRef.current) return;

            if (result && result.error) {
                setIsGenerating(false);
                setGenerationProgress(0);
                showErrorNotification({ message: T._('An error occurred while generating the 3D model. Please try again.', 'Admin') });
                return;
            }

            setGenerationProgress(100);
            setIsGenerating(false);
            setTokens((prev) => Math.max(0, prev - (Math.floor(Math.random() * 4) + 2)));
            generatedCountRef.current += 1;
            const newId = result?.id ?? `model-${Date.now()}`;
            const label = `Model ${generatedCountRef.current}`;
            const modelUrl = result?.url || undefined;
            // Prepend the new model into the next free slot (newest first) so previous
            // generations stay visible instead of being overwritten, capped at MAX_3D_SLOTS.
            setGeneratedModels((prev) =>
                [{ id: newId, label, toolId: selectedAgent, modelUrl, prompt, createdAt: Date.now() }, ...prev].slice(0, MAX_3D_SLOTS)
            );
            // Backend persisted the model via saveToLibrary; invalidate the Relay cache so the
            // asset library refetches and shows the new 3D asset (with a real GlbViewer preview)
            // instead of just the local placeholder.
            commitLocalUpdate(relayEnvironment, (store) => {
                store.invalidateStore();
            });
            onModelGenerated?.(newId, selectedAgent, label, undefined, prompt, modelUrl);
        } catch (err) {
            stopTimers();
            if (stoppedRef.current) return;
            setIsGenerating(false);
            setGenerationProgress(0);
            showErrorNotification({ message: T._('An error occurred while generating the 3D model. Please try again.', 'Admin') });
        }
    }, [selectedAgent, onModelGenerated, prompt, imageAgentFile, mvFiles, selectedGenerationModel, selectedTaskParameters, relayEnvironment]);

    // Runs the generation pipeline gated by addon activation + service availability checks.
    const handleGenerate = useCallback(async () => {
        if (isGenerating) return;
        if (selectedAgent === 'text-to-3d' && !prompt.trim()) return;
        if (selectedAgent === 'image-to-3d' && !imageAgentFile) return;
        if (selectedAgent === 'multi-views-to-3d' && mvFiles.length === 0) return;

        await runWithPreflight(selectedAgent as ToolType, executeGeneration, {
            onAddonActivationStarted: () => {
                // Show the generating UI immediately so the click feels responsive while the
                // activation mutation is in flight; executeGeneration will take over once it runs.
                setIsGenerating(true);
                setGenerationProgress(0);
                setGenerationMsgIdx(0);
            },
            onLimitReached: (serviceName) => {
                showDialog('fair-use-limit-dialog', <FairUseLimitDialog serviceName={serviceName} onClose={() => closeDialog('fair-use-limit-dialog')} />);
            }
        });
    }, [isGenerating, selectedAgent, prompt, imageAgentFile, mvFiles, selectedGenerationModel, runWithPreflight, executeGeneration, showDialog, closeDialog]);

    // Keep the ref in sync so the library auto-start effect always calls the latest closure.
    handleGenerateRef.current = handleGenerate;

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            const t = generationTimersRef.current;
            if (t.msg) clearInterval(t.msg);
            if (t.progress) clearInterval(t.progress);
            if (t.finalize) clearTimeout(t.finalize);
        };
    }, []);

    // Auto-start generation when arriving from the landing view with pre-filled input.
    // Covers text-to-3d (initialPrompt), image-to-3d (initialImageFile), and multi-views-to-3d (initialMultiViewReady).
    // Skipped when an existing asset is being opened (no generation needed).
    // Uses handleGenerateRef (not handleGenerate directly) so a re-render inside the 300ms
    // window can't cancel the scheduled timeout via the effect cleanup.
    const autoStartedRef = useRef(false);
    useEffect(() => {
        if (autoStartedRef.current || openedAsset) return;
        const shouldAutoStart = !!(initialPrompt && initialPrompt.trim().length > 0) || !!initialImageFile || !!initialImageUrl || !!initialMultiViewReady;
        if (shouldAutoStart) {
            autoStartedRef.current = true;
            // Small delay so the workspace has time to mount and animate in
            const t = setTimeout(() => {
                setPendingAutoStart(false);
                handleGenerateRef.current();
            }, 300);
            return () => clearTimeout(t);
        }
    }, [initialPrompt, initialImageFile, initialImageUrl, initialMultiViewReady, openedAsset]);

    const [setMenuOpen, setMenuClose, isMenuOpen, ViewportDropdown] = useDropdown();

    // Which model's action menu is currently open. The single useDropdown instance positions
    // the portal against the clicked button, so we only need to track the target model here.
    const [menuModel, setMenuModel] = useState<LocalModel3DItem | null>(null);

    const handleMenuToggle = useCallback(
        (e: React.MouseEvent, model: LocalModel3DItem) => {
            e.stopPropagation();
            if (isMenuOpen && menuModel?.id === model.id) {
                setMenuClose();
                setMenuModel(null);
            } else {
                setMenuModel(model);
                setMenuOpen(e.currentTarget as HTMLElement, 'top', 'left');
            }
        },
        [isMenuOpen, menuModel, setMenuOpen, setMenuClose]
    );

    const handleDownload3D = useCallback(
        (model: LocalModel3DItem) => {
            setMenuClose();
            setMenuModel(null);
            if (model.modelUrl) {
                downloadFile(model.modelUrl, `3d-model-${Date.now()}.glb`);
            }
        },
        [setMenuClose]
    );

    const handleDelete3D = useCallback(
        (model: LocalModel3DItem) => {
            setMenuClose();
            setMenuModel(null);
            setGeneratedModels((prev) => prev.filter((m) => m.id !== model.id));
        },
        [setMenuClose]
    );

    const isGeneratingPhase = isGenerating || pendingAutoStart;

    const [previewIndex, setPreviewIndex] = useState<number | null>(null);
    const [isInputImagePreviewOpen, setIsInputImagePreviewOpen] = useState(false);

    const handleOpenInputImagePreview = useCallback(() => {
        setIsInputImagePreviewOpen(true);
    }, []);

    const handleCloseInputImagePreview = useCallback(() => {
        setIsInputImagePreviewOpen(false);
    }, []);

    const renderModelOverlay = (model: LocalModel3DItem, idx: number) => (
        <>
            <ViewportMenuWrapper>
                <ActionButton onClick={(e) => handleMenuToggle(e, model)}>
                    <EllipsisVIcon />
                </ActionButton>
            </ViewportMenuWrapper>
            <ViewportFullscreenBtn onClick={() => setPreviewIndex(idx)} title={T._('Preview', 'Admin')}>
                <Icon>
                    <ExpandIcon />
                </Icon>
            </ViewportFullscreenBtn>
        </>
    );

    const generatingCount = isGeneratingPhase ? 1 : 0;
    // Cap visible models so the generating placeholder + models never exceed the 4-cell grid.
    const visibleModels = generatedModels.slice(0, MAX_3D_SLOTS - generatingCount);
    const emptySlotCount = Math.max(0, MAX_3D_SLOTS - visibleModels.length - generatingCount);

    return (
        <WorkspaceShell>
            <Workspace>
                <ViewportRow>
                    <ModelResultsGrid>
                        {isGeneratingPhase && (
                            <GeneratingModelCard>
                                <GeneratingCardText key={generationMsgIdx}>{T._(LOADING_MESSAGES[generationMsgIdx], 'Admin')}</GeneratingCardText>
                                <GeneratingCardProgressContainer>
                                    <GeneratingCardProgressBar $width={generationProgress} />
                                </GeneratingCardProgressContainer>
                            </GeneratingModelCard>
                        )}
                        {visibleModels.map((model, idx) => (
                            <ModelCell key={model.id}>
                                {model.modelUrl ? (
                                    <RealModelViewport>
                                        <Suspense fallback={null}>
                                            <GlbViewer url={model.modelUrl} />
                                        </Suspense>
                                        {renderModelOverlay(model, idx)}
                                    </RealModelViewport>
                                ) : (
                                    <Viewport3D key={model.id} modelGenerated overlayContent={renderModelOverlay(model, idx)} agentType={model.toolId} />
                                )}
                            </ModelCell>
                        ))}
                        {Array.from({ length: emptySlotCount }).map((_, i) => (
                            <EmptyModelCard key={`empty-${i}`} />
                        ))}
                    </ModelResultsGrid>
                </ViewportRow>

                <PromptPanel>
                    <PromptCard>
                        {isMultiViewAgent ? (
                            <>
                                <MultiViewDropZone
                                    initialSlots={mvInitialSlotsRef.current}
                                    onAnyLoading={setMultiViewLoading}
                                    onSlotsChange={(slots) => {
                                        // Sync the drop zone's slot state into mvFiles. Preserves file order
                                        // by VIEW_SLOTS (front first), so handleGenerate can use the first entry
                                        // as the front/imageUrl and the rest as multiViewImages with their viewType.
                                        setMvFiles((prev) => {
                                            const next: Array<{ id: string; file: File; previewUrl: string; name: string; viewType: string }> = [];
                                            VIEW_SLOTS.forEach((slot) => {
                                                const entry = slots[slot.id];
                                                if (!entry) return;
                                                const existing = prev.find((f) => f.viewType === slot.id && f.file === entry.file);
                                                if (existing) {
                                                    next.push(existing);
                                                } else {
                                                    next.push({
                                                        id: `mv-${slot.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                                                        file: entry.file,
                                                        previewUrl: entry.previewUrl,
                                                        name: entry.name,
                                                        viewType: slot.id
                                                    });
                                                }
                                            });
                                            // Revoke blob URLs for removed entries we created ourselves.
                                            prev.forEach((p) => {
                                                if (!next.some((n) => n.id === p.id) && p.id.startsWith('mv-init-')) {
                                                    URL.revokeObjectURL(p.previewUrl);
                                                }
                                            });
                                            return next;
                                        });
                                    }}
                                />
                                {mvFiles.length > 0 && (
                                    <PromptRowHoriz>
                                        <PromptInput value='' onChange={() => {}} placeholder={T._('Prompt not supported for this tool', 'Admin')} disabled />
                                        {isGenerating ? (
                                            <GenerateBtn $isGenerating onClick={handleStopGenerate} title={T._('Stop', 'Admin')}>
                                                <Icon>
                                                    <StopIcon />
                                                </Icon>
                                            </GenerateBtn>
                                        ) : (
                                            <GenerateBtn
                                                onClick={handleGenerate}
                                                disabled={!mvFiles.some((f) => f.viewType === 'front') || multiViewLoading || !selectedGenerationModel}
                                                title={
                                                    multiViewLoading
                                                        ? T._('Waiting for image upload to finish…', 'Admin')
                                                        : mvFiles.some((f) => f.viewType === 'front')
                                                          ? T._('Generate', 'Admin')
                                                          : T._('Upload the Front view first', 'Admin')
                                                }
                                            >
                                                <Icon>
                                                    <PaperPlaneIcon />
                                                </Icon>
                                            </GenerateBtn>
                                        )}
                                    </PromptRowHoriz>
                                )}
                            </>
                        ) : isImageAgent ? (
                            imageAgentFile ? (
                                <>
                                    <InputPreviews>
                                        <InputPreviewItem>
                                            <InputPreviewImage $clickable onClick={handleOpenInputImagePreview} title={T._('Open preview', 'Admin')}>
                                                <img src={imageAgentFile.previewUrl} alt={imageAgentFile.name} />
                                            </InputPreviewImage>
                                            <InputPreviewRemove
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleImageAgentRemove();
                                                }}
                                                title={T._('Remove', 'Admin')}
                                            >
                                                <Icon>
                                                    <TimesIcon />
                                                </Icon>
                                            </InputPreviewRemove>
                                        </InputPreviewItem>
                                    </InputPreviews>
                                    <PromptRowHoriz>
                                        <PromptInput value='' onChange={() => {}} placeholder={T._('Prompt not supported for this tool', 'Admin')} disabled />
                                        {isGenerating ? (
                                            <GenerateBtn $isGenerating onClick={handleStopGenerate} title={T._('Stop', 'Admin')}>
                                                <Icon>
                                                    <StopIcon />
                                                </Icon>
                                            </GenerateBtn>
                                        ) : (
                                            <GenerateBtn onClick={handleGenerate} disabled={!selectedGenerationModel} title={T._('Generate', 'Admin')}>
                                                <Icon>
                                                    <PaperPlaneIcon />
                                                </Icon>
                                            </GenerateBtn>
                                        )}
                                    </PromptRowHoriz>
                                </>
                            ) : (
                                <ImageUploadZones
                                    onImageUpload={handleImageAgentUpload}
                                    onOpenCatalog={onOpenCatalog}
                                    onOpenLibrary={onOpenImageLibrary ? handleImageAgentLibrarySelect : undefined}
                                    maxSizeMB={MAX_3D_UPLOAD_SIZE_MB}
                                    maxDimension={MAX_3D_IMAGE_DIMENSION}
                                />
                            )
                        ) : (
                            <>
                                <PromptRowHoriz>
                                    <InputWithDiceWrapper>
                                        <PromptInput
                                            value={prompt}
                                            onChange={setPrompt}
                                            placeholder={T._('Describe the 3D model you want to generate — e.g. "a ceramic vase with blue glaze"', 'Admin')}
                                        />
                                    </InputWithDiceWrapper>
                                    {isGenerating ? (
                                        <GenerateBtn $isGenerating onClick={handleStopGenerate} title={T._('Stop', 'Admin')}>
                                            <Icon>
                                                <StopIcon />
                                            </Icon>
                                        </GenerateBtn>
                                    ) : (
                                        <GenerateBtn
                                            onClick={handleGenerate}
                                            disabled={!prompt.trim() || !selectedGenerationModel}
                                            title={T._('Generate', 'Admin')}
                                        >
                                            <Icon>
                                                <PaperPlaneIcon />
                                            </Icon>
                                        </GenerateBtn>
                                    )}
                                </PromptRowHoriz>
                                {selectedAgent === 'text-to-3d' && (
                                    <DiceRow>
                                        <DiceBtn
                                            type='button'
                                            onClick={() => setPrompt(TEXT_TO_3D_RANDOM_PROMPTS[Math.floor(Math.random() * TEXT_TO_3D_RANDOM_PROMPTS.length)])}
                                        >
                                            <Icon>
                                                <DiceIcon />
                                            </Icon>
                                            <DiceBtnLabel>{T._('Try random prompt', 'Admin')}</DiceBtnLabel>
                                        </DiceBtn>
                                        <AiStudioHelpLink agentId={selectedAgent} />
                                    </DiceRow>
                                )}
                            </>
                        )}
                        {selectedAgent !== 'text-to-3d' && (
                            <AiStudioHelpRow>
                                <AiStudioHelpLink agentId={selectedAgent} />
                            </AiStudioHelpRow>
                        )}
                    </PromptCard>
                </PromptPanel>

                {isMenuOpen && menuModel && (
                    <ViewportDropdown>
                        <DropdownMenuItem onClick={() => handleDownload3D(menuModel)}>
                            <DownloadIcon />
                            {T._('Download', 'Admin')}
                        </DropdownMenuItem>
                        <DropdownMenuItem $danger onClick={() => handleDelete3D(menuModel)}>
                            <TrashIcon />
                            {T._('Delete', 'Admin')}
                        </DropdownMenuItem>
                    </ViewportDropdown>
                )}

                {previewIndex !== null && generatedModels[previewIndex] && (
                    <Local3DPreviewDialog items={generatedModels} index={previewIndex} onClose={() => setPreviewIndex(null)} onIndexChange={setPreviewIndex} />
                )}

                {isInputImagePreviewOpen && imageAgentFile && (
                    <ImagePreviewBackdrop onClick={handleCloseInputImagePreview}>
                        <ImagePreviewClose onClick={handleCloseInputImagePreview} title={T._('Close', 'Admin')}>
                            <Icon>
                                <TimesIcon />
                            </Icon>
                        </ImagePreviewClose>
                        <img src={imageAgentFile.previewUrl} alt={imageAgentFile.name} onClick={(e) => e.stopPropagation()} />
                    </ImagePreviewBackdrop>
                )}
            </Workspace>
            <Model3DSettingsSidebar
                agentId={selectedAgent}
                models={modelGeneration3DModels}
                selectedModel={selectedGenerationModel}
                onModelChange={onModelChange}
                parameterValues={parameterValues}
                onParameterValuesChange={onParameterValuesChange}
            />
        </WorkspaceShell>
    );
};

export default Text3DWorkspaceView;
